import type { TProjectStatus } from '@feathr/extender/components/ProjectStatusSegmentedControl';
import { useStore } from '@feathr/extender/state';
import type { IObject } from '@feathr/rachis';

import { buildProjectStatusFilter } from './EventsPage.utils';

interface IProjectStatusFilters {
  name__icontains?: string;
  tag_ids__in: string[];
}

interface IUseProjectStatusCountsArgs {
  filters: IProjectStatusFilters;
}

interface IUseProjectStatusCountsResult {
  activeCount: number;
  archivedCount: number;
}

/**
 * Merges the name/tag filters shared with the Projects list with the is_hidden
 * filter for a given segment (see {@link buildProjectStatusFilter}), dropping
 * empty values so they don't reach the query string.
 */
function buildStatusFilters(filters: IProjectStatusFilters, status: TProjectStatus): IObject {
  const baseFilters: IObject = {};

  if (filters.name__icontains) {
    baseFilters.name__icontains = filters.name__icontains;
  }
  if (filters.tag_ids__in.length > 0) {
    baseFilters.tag_ids__in = filters.tag_ids__in;
  }

  return { ...baseFilters, ...buildProjectStatusFilter(status) };
}

/**
 * Returns the Active (non-hidden) and Archived (hidden) project counts under the
 * current name/tag filters so both segment badges show a number. Each is a
 * count-only fetch (`Collection.count` reads `pagination.count` from a single-row
 * page) and is MobX-reactive — the consuming component must be an `observer` to
 * re-render when the counts resolve. Counts read 0 while loading or empty.
 */
export function useProjectStatusCounts({
  filters,
}: IUseProjectStatusCountsArgs): IUseProjectStatusCountsResult {
  const { Events } = useStore();

  const activeCount = Events.count({ filters: buildStatusFilters(filters, 'active') });
  const archivedCount = Events.count({ filters: buildStatusFilters(filters, 'archived') });

  return {
    activeCount,
    archivedCount,
  };
}

export default useProjectStatusCounts;
